import { getVersion } from '@tauri-apps/api/app';

import { compareVersions } from '@/tauri/force-update/versionComparison';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';

import type {
  ChronicleFirmwareVersion,
  ChronicleRemoteConfig,
  FirmwareCheckResult,
  FirmwareUpdateType,
} from './types';

// ── Config ──────────────────────────────────────────────────────────────

const CONFIG_KEY = 'chronicle';

// ── Cache ───────────────────────────────────────────────────────────────

const configCache: { data: ChronicleRemoteConfig | null; timestamp: number } = {
  data: null,
  timestamp: 0,
};

export function clearFirmwareCatalogCache(): void {
  configCache.data = null;
  configCache.timestamp = 0;
}

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Check if a firmware update is available for the given device.
 *
 * Reads `versions_v2` exclusively and applies the per-platform `min_app_version.desktop`
 * gate against the running Tauri app version (sourced from `getVersion()` in
 * `@tauri-apps/api/app`). Per the APP-5036 design, the legacy `versions` array is
 * intentionally ignored on v2-aware Desktop builds — older un-updated installs in the
 * field continue to read it, but this client never does.
 *
 * Tests override the running app version via `jest.mock('@tauri-apps/api/app', ...)` —
 * see `firmware-catalog.test.ts` — so this function does not expose a test-only
 * injection parameter on its public signature.
 *
 * @param currentVersion - The device's current firmware version (e.g., "1.0").
 * @param deviceModel - Device model to match against config entries (e.g., "HV0"). When
 *   absent, returns no-update to avoid selecting the wrong firmware.
 */
export async function checkFirmwareUpdate(
  currentVersion: string,
  deviceModel?: string
): Promise<FirmwareCheckResult> {
  const noUpdate: FirmwareCheckResult = {
    currentVersion,
    latestVersion: null,
    updateAvailable: false,
    updateType: null,
    downloadUrl: null,
    whatsNew: null,
  };

  if (!deviceModel) {
    logger.warn({
      msg: '[FirmwareCatalog] No device model provided — skipping firmware check',
    });
    return noUpdate;
  }

  let config: ChronicleRemoteConfig | null;
  try {
    config = await fetchChronicleConfig();
  } catch {
    return noUpdate;
  }
  if (!config) return noUpdate;

  const appVersion = await resolveDesktopAppVersion();
  if (appVersion === null) {
    // Without the running app version we cannot enforce the min_app_version gate;
    // fail closed so we never surface a firmware that the running app may not support.
    logger.warn({
      msg: '[FirmwareCatalog] Could not resolve desktop app version — skipping firmware check',
    });
    return noUpdate;
  }

  const firmware = pickEligibleV2Entry(config.versions_v2 ?? [], deviceModel, appVersion);

  if (!firmware) {
    logger.debug({
      msg: '[FirmwareCatalog] No eligible v2 firmware entry',
      deviceModel,
      desktopAppVersion: appVersion,
      candidateModels: (config.versions_v2 ?? []).map((v) => v.model),
    });
    return noUpdate;
  }

  const latestVersion = firmware.version;
  const comparison = compareVersions(currentVersion, latestVersion);

  if (comparison === null || comparison >= 0) {
    return { ...noUpdate, latestVersion, updateAvailable: false };
  }

  return {
    currentVersion,
    latestVersion,
    updateAvailable: true,
    updateType: determineUpdateType(currentVersion, firmware.required_version),
    downloadUrl: config.base_url + firmware.uri,
    whatsNew: firmware.whats_new ?? null,
  };
}

// ── Internal helpers ────────────────────────────────────────────────────

async function fetchChronicleConfig(): Promise<ChronicleRemoteConfig | null> {
  // TELEMETRY-GATE: F1 load-site gate — Firebase must never load in a
  // suppressed build (it phones home to Firebase Remote Config). Short-circuit
  // before the dynamic import so the SDK is never fetched or evaluated; the
  // inner guard in firebase.ts would only no-op it after the chunk loaded.
  if (!isSaasTelemetryEnabled) return null;
  try {
    // Dynamic import: every consumer of @/lib/firebase (this catalog and the
    // force-update policy fetch) loads it lazily, code-splitting the entire
    // Firebase SDK out of the boot chunk.
    const { fetchRemoteConfigValue } = await import('@/lib/firebase');
    const config = await fetchRemoteConfigValue<ChronicleRemoteConfig>(
      CONFIG_KEY,
      configCache
    );

    if (!config?.versions || !Array.isArray(config.versions)) {
      logger.warn({
        msg: '[FirmwareCatalog] Invalid config structure — missing versions array',
      });
      configCache.data = null;
      return null;
    }

    logger.info({
      msg: '[FirmwareCatalog] Config fetched',
      versionsCount: config.versions.length,
      versionsV2Count: config.versions_v2?.length ?? 0,
    });

    return config;
  } catch (error) {
    logger.error({
      msg: '[FirmwareCatalog] Failed to fetch firmware config',
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

/**
 * Returns the highest-version `versions_v2` entry matching `deviceModel` whose
 * `min_app_version.desktop` gate (if any) is satisfied by `desktopAppVersion`.
 */
function pickEligibleV2Entry(
  v2: ChronicleFirmwareVersion[],
  deviceModel: string,
  desktopAppVersion: string
): ChronicleFirmwareVersion | undefined {
  const eligible = v2.filter(
    (entry) =>
      entry.model.toLowerCase() === deviceModel.toLowerCase() &&
      isDesktopGateMet(entry, desktopAppVersion)
  );
  if (eligible.length === 0) return undefined;
  return eligible.reduce((best, current) => {
    const cmp = compareVersions(current.version, best.version);
    return cmp !== null && cmp > 0 ? current : best;
  });
}

function isDesktopGateMet(
  entry: ChronicleFirmwareVersion,
  desktopAppVersion: string
): boolean {
  const minDesktop = entry.min_app_version?.desktop;
  // Missing min_app_version, or missing `desktop` key inside it ⇒ no gate ⇒ visible.
  if (!minDesktop) return true;
  const cmp = compareVersions(desktopAppVersion, minDesktop);
  // semver lib returned null (unparseable) ⇒ fail closed: do not surface.
  return cmp !== null && cmp >= 0;
}

async function resolveDesktopAppVersion(): Promise<string | null> {
  try {
    return await getVersion();
  } catch (error) {
    logger.error({
      msg: '[FirmwareCatalog] getVersion() failed',
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

function determineUpdateType(
  currentVersion: string,
  requiredVersion?: string
): FirmwareUpdateType {
  if (requiredVersion) {
    const comparison = compareVersions(currentVersion, requiredVersion);
    if (comparison !== null && comparison < 0) return 'required';
  }
  return 'recommended';
}
